import { useEffect, useRef, useState, type CSSProperties } from 'react';
import { Link } from 'react-router-dom';
import toast from 'react-hot-toast';
import {
  closestCenter,
  DndContext,
  DragEndEvent,
  DragOverlay,
  DragStartEvent,
  PointerSensor,
  useDroppable,
  useSensor,
  useSensors,
} from '@dnd-kit/core';
import { arrayMove, SortableContext, useSortable, verticalListSortingStrategy } from '@dnd-kit/sortable';
import { CSS } from '@dnd-kit/utilities';
import {
  flowFetch, FlowArea, FlowUser, FlowTask, FlowColumn,
  PRIORITY_LABELS, PRIORITY_COLORS, formatDate, isOverdue
} from './flowApi';
import { TaskFormModal, TaskDetailModal } from './FlowTasks';

const KIND_OPTIONS: { value: string; label: string }[] = [
  { value: 'TODO', label: 'A fazer' },
  { value: 'DOING', label: 'Fazendo' },
  { value: 'WAITING', label: 'Aguardando' },
  { value: 'DONE', label: 'Concluído' },
];

const KIND_ACCENT: Record<string, string> = {
  TODO: 'border-t-slate-400',
  DOING: 'border-t-blue-500',
  WAITING: 'border-t-amber-500',
  DONE: 'border-t-emerald-500',
};

function initials(name?: string) {
  if (!name) return '?';
  const parts = name.trim().split(' ');
  return (parts[0][0] + (parts[1]?.[0] || '')).toUpperCase();
}

// ---------- Card do Kanban ----------
function TaskCard({ task, onOpen }: {
  task: FlowTask;
  onOpen: () => void;
}) {
  const { attributes, listeners, setNodeRef, transform, transition, isDragging } = useSortable({ id: task.id });
  const overdue = isOverdue(task);
  const style: CSSProperties = {
    transform: CSS.Transform.toString(transform),
    transition,
    opacity: isDragging ? 0.35 : 1,
  };
  return (
    <div
      ref={setNodeRef}
      data-task-id={task.id}
      style={style}
      {...attributes}
      {...listeners}
      onClick={onOpen}
      className="cursor-grab rounded-xl border border-slate-200 bg-white p-3 shadow-sm transition hover:shadow-md active:cursor-grabbing">
      {task.isDaily && (
        <span className="mb-1 inline-block rounded bg-violet-100 px-1.5 py-0.5 text-[9px] font-bold uppercase tracking-wide text-violet-700">Diária</span>
      )}
      {/* Subtarefa: mostra a tarefa principal acima do título */}
      {(task as any).parent && (
        <p className="mb-0.5 flex items-center gap-1 text-[11px] font-semibold text-violet-600" title={`Subtarefa de: ${(task as any).parent.title}`}>
          <svg className="h-3 w-3 shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24" strokeWidth={2}><path strokeLinecap="round" strokeLinejoin="round" d="M7 16l-4-4m0 0l4-4m-4 4h18" /></svg>
          <span className="truncate">{(task as any).parent.title}</span>
        </p>
      )}
      <div className="mb-2 flex items-start justify-between gap-2">
        <p className="text-sm font-semibold leading-snug text-slate-800">{task.title}</p>
        <span className={`shrink-0 rounded-full px-1.5 py-0.5 text-[10px] font-bold ${PRIORITY_COLORS[task.priority]}`}>{PRIORITY_LABELS[task.priority]}</span>
      </div>
      <div className="flex flex-wrap items-center gap-2 text-[11px] text-slate-500">
        {task.timerStartedAt && (
          <span className="inline-flex items-center gap-1 rounded bg-emerald-50 px-1.5 py-0.5 font-bold text-emerald-600">⏱ rodando</span>
        )}
        {task.dueDate && (
          <span className={`inline-flex items-center gap-1 rounded px-1.5 py-0.5 ${overdue ? 'bg-red-50 font-bold text-red-600' : 'bg-slate-100'}`}>
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M8 7V3m8 4V3m-9 8h10M5 21h14a2 2 0 002-2V7a2 2 0 00-2-2H5a2 2 0 00-2 2v12a2 2 0 002 2z" /></svg>
            {formatDate(task.dueDate)}
          </span>
        )}
        {(task.checklistTotal ?? 0) > 0 && (
          <span className="inline-flex items-center gap-1">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M9 12l2 2 4-4" /></svg>
            {task.checklistDone}/{task.checklistTotal}
          </span>
        )}
        {(task.commentCount ?? 0) > 0 && (
          <span className="inline-flex items-center gap-1">
            <svg className="h-3 w-3" fill="none" stroke="currentColor" viewBox="0 0 24 24"><path strokeLinecap="round" strokeWidth={2} d="M8 12h.01M12 12h.01M16 12h.01M21 12c0 4.418-4.03 8-9 8a9.863 9.863 0 01-4.255-.949L3 20l1.395-3.72C3.512 15.042 3 13.574 3 12c0-4.418 4.03-8 9-8s9 3.582 9 8z" /></svg>
            {task.commentCount}
          </span>
        )}
        {task.assignee && (
          <span className="ml-auto flex h-6 w-6 items-center justify-center rounded-full bg-cyan-100 text-[10px] font-bold text-cyan-700" title={task.assignee.nome}>
            {initials(task.assignee.nome)}
          </span>
        )}
      </div>
    </div>
  );
}

function TaskColumnBody({ columnId, taskIds, tasksById, onOpen, onAdd }: {
  columnId: string;
  taskIds: string[];
  tasksById: Record<string, FlowTask>;
  onOpen: (taskId: string) => void;
  onAdd: () => void;
}) {
  const { setNodeRef, isOver } = useDroppable({ id: `column:${columnId}`, data: { columnId } });

  return (
    <div
      ref={setNodeRef}
      data-column-id={columnId}
      className={`flex-1 space-y-2 rounded-b-xl border border-t-0 p-2 transition ${isOver ? 'border-cyan-400 bg-cyan-50/60' : 'border-slate-200 bg-slate-50/60'}`}
      style={{ minHeight: 120 }}
    >
      <SortableContext items={taskIds} strategy={verticalListSortingStrategy}>
        {taskIds.map(taskId => {
          const task = tasksById[taskId];
          return task ? <TaskCard key={taskId} task={task} onOpen={() => onOpen(taskId)} /> : null;
        })}
      </SortableContext>
      <button onClick={onAdd}
        className="w-full rounded-lg border border-dashed border-slate-300 py-2 text-xs font-semibold text-slate-400 hover:border-cyan-400 hover:text-cyan-600">
        + Adicionar tarefa
      </button>
    </div>
  );
}

// ---------- Coluna ----------
function ColumnHeader({ column, count, isAdmin, onRename, onChangeKind, onArchive, onMove }: {
  column: FlowColumn; count: number; isAdmin: boolean;
  onRename: (name: string) => void; onChangeKind: (kind: string) => void; onArchive: () => void; onMove: (dir: number) => void;
}) {
  const [menu, setMenu] = useState(false);
  const [editing, setEditing] = useState(false);
  const [name, setName] = useState(column.name);

  return (
    <div className={`flex items-center justify-between gap-2 rounded-t-xl border-t-4 bg-white px-3 py-2 ${KIND_ACCENT[column.kind] || 'border-t-slate-300'}`}>
      {editing ? (
        <input autoFocus value={name} onChange={e => setName(e.target.value)}
          onBlur={() => { setEditing(false); if (name.trim() && name !== column.name) onRename(name.trim()); }}
          onKeyDown={e => { if (e.key === 'Enter') { setEditing(false); if (name.trim() && name !== column.name) onRename(name.trim()); } }}
          className="w-full rounded border border-slate-300 px-1.5 py-0.5 text-sm font-bold" />
      ) : (
        <div className="flex min-w-0 items-center gap-2">
          <span className="truncate text-sm font-bold text-slate-700">{column.name}</span>
          <span className="rounded-full bg-slate-100 px-1.5 text-xs font-semibold text-slate-500">{count}</span>
        </div>
      )}
      {isAdmin && !editing && (
        <div className="relative">
          <button onClick={() => setMenu(!menu)} className="rounded p-1 text-slate-400 hover:bg-slate-100">
            <svg className="h-4 w-4" fill="currentColor" viewBox="0 0 20 20"><path d="M10 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4zm0 6a2 2 0 110-4 2 2 0 010 4z" /></svg>
          </button>
          {menu && (
            <div className="absolute right-0 z-30 mt-1 w-48 rounded-xl border border-slate-200 bg-white py-1 shadow-xl" onMouseLeave={() => setMenu(false)}>
              <button onClick={() => { setEditing(true); setMenu(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50">Renomear</button>
              <button onClick={() => { onMove(-1); setMenu(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50">Mover para esquerda</button>
              <button onClick={() => { onMove(1); setMenu(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-slate-700 hover:bg-slate-50">Mover para direita</button>
              <div className="my-1 border-t border-slate-100" />
              <p className="px-3 py-1 text-[10px] font-bold uppercase text-slate-400">Comportamento</p>
              {KIND_OPTIONS.map(k => (
                <button key={k.value} onClick={() => { onChangeKind(k.value); setMenu(false); }}
                  className={`block w-full px-3 py-1.5 text-left text-sm hover:bg-slate-50 ${column.kind === k.value ? 'font-bold text-cyan-700' : 'text-slate-700'}`}>
                  {k.label} {column.kind === k.value && '✓'}
                </button>
              ))}
              <div className="my-1 border-t border-slate-100" />
              <button onClick={() => { onArchive(); setMenu(false); }} className="block w-full px-3 py-1.5 text-left text-sm text-red-600 hover:bg-red-50">Arquivar coluna</button>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

// ---------- Kanban ----------
export function FlowKanban({ boardId, areas, users, isAdmin }: { boardId: string; areas: FlowArea[]; users: FlowUser[]; isAdmin: boolean }) {
  const [data, setData] = useState<any>(null);
  const [loading, setLoading] = useState(true);
  const [taskOrder, setTaskOrder] = useState<Record<string, string[]>>({});
  const [activeTaskId, setActiveTaskId] = useState<string | null>(null);
  const orderSnapshotRef = useRef<Record<string, string[]> | null>(null);
  const [detailId, setDetailId] = useState<string | null>(null);
  const [createCol, setCreateCol] = useState<string | null>(null);
  const [addingColumn, setAddingColumn] = useState(false);
  const [newColName, setNewColName] = useState('');
  const [newColKind, setNewColKind] = useState('TODO');
  // filtros
  const [assigneeFilter, setAssigneeFilter] = useState('');
  const [priorityFilter, setPriorityFilter] = useState('');
  const [dueFilter, setDueFilter] = useState('');
  const [search, setSearch] = useState('');
  const sensors = useSensors(useSensor(PointerSensor, { activationConstraint: { distance: 5 } }));

  const load = async () => {
    try {
      const res = await flowFetch(`/api/flow/boards/${boardId}`);
      setData(res);
      const nextOrder: Record<string, string[]> = {};
      (res.columns as FlowColumn[]).forEach(column => { nextOrder[column.id] = []; });
      (res.tasks as FlowTask[]).forEach(task => {
        if (task.columnId && nextOrder[task.columnId]) nextOrder[task.columnId].push(task.id);
      });
      setTaskOrder(nextOrder);
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao carregar quadro');
    } finally {
      setLoading(false);
    }
  };
  useEffect(() => { setLoading(true); load(); }, [boardId]);

  const applyFilters = (tasks: FlowTask[]) => tasks.filter(t => {
    if (assigneeFilter && t.assigneeId !== assigneeFilter) return false;
    if (priorityFilter && t.priority !== priorityFilter) return false;
    if (search && !t.title.toLowerCase().includes(search.toLowerCase())) return false;
    if (dueFilter === 'overdue' && !isOverdue(t)) return false;
    if (dueFilter === 'today') {
      if (!t.dueDate) return false;
      const d = new Date(t.dueDate); const now = new Date();
      if (d.toDateString() !== now.toDateString()) return false;
    }
    if (dueFilter === 'none' && t.dueDate) return false;
    return true;
  });

  const findTaskColumn = (taskId: string) => {
    for (const [columnId, taskIds] of Object.entries(taskOrder)) {
      if (taskIds.includes(taskId)) return columnId;
    }
    return null;
  };

  const handleDragStart = (event: DragStartEvent) => {
    setActiveTaskId(String(event.active.id));
    orderSnapshotRef.current = Object.fromEntries(
      Object.entries(taskOrder).map(([columnId, taskIds]) => [columnId, [...taskIds]])
    );
  };

  const handleDragEnd = async (event: DragEndEvent) => {
    const { active, over } = event;
    setActiveTaskId(null);
    if (!over) return;

    const taskId = String(active.id);
    const overId = String(over.id);
    const sourceColumnId = findTaskColumn(taskId);
    if (!sourceColumnId) return;

    const destinationColumnId = overId.startsWith('column:')
      ? overId.slice('column:'.length)
      : findTaskColumn(overId);
    if (!destinationColumnId) return;

    const sourceIds = taskOrder[sourceColumnId] || [];
    const destinationIds = taskOrder[destinationColumnId] || [];
    let destinationIndex: number;

    if (sourceColumnId === destinationColumnId) {
      const oldIndex = sourceIds.indexOf(taskId);
      const newIndex = overId.startsWith('column:') ? sourceIds.length - 1 : sourceIds.indexOf(overId);
      if (oldIndex < 0 || newIndex < 0 || oldIndex === newIndex) return;
      destinationIndex = newIndex;
      setTaskOrder(previous => ({ ...previous, [sourceColumnId]: arrayMove(sourceIds, oldIndex, newIndex) }));
    } else {
      destinationIndex = overId.startsWith('column:') ? destinationIds.length : destinationIds.indexOf(overId);
      if (destinationIndex < 0) destinationIndex = destinationIds.length;
      const nextSource = sourceIds.filter(id => id !== taskId);
      const nextDestination = [...destinationIds];
      nextDestination.splice(destinationIndex, 0, taskId);
      setTaskOrder(previous => ({
        ...previous,
        [sourceColumnId]: nextSource,
        [destinationColumnId]: nextDestination,
      }));
    }

    try {
      await flowFetch(`/api/flow/tasks/${taskId}/move`, {
        method: 'PUT',
        body: JSON.stringify({ columnId: destinationColumnId, position: destinationIndex })
      });
    } catch (err) {
      toast.error(err instanceof Error ? err.message : 'Erro ao mover tarefa');
      if (orderSnapshotRef.current) setTaskOrder(orderSnapshotRef.current);
      await load();
    }
  };

  const renameColumn = async (id: string, name: string) => {
    try { await flowFetch(`/api/flow/columns/${id}`, { method: 'PUT', body: JSON.stringify({ name }) }); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro'); }
  };
  const changeKind = async (id: string, kind: string) => {
    try { await flowFetch(`/api/flow/columns/${id}`, { method: 'PUT', body: JSON.stringify({ kind }) }); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro'); }
  };
  const archiveColumn = async (id: string) => {
    try { await flowFetch(`/api/flow/columns/${id}`, { method: 'PUT', body: JSON.stringify({ archived: true }) }); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao arquivar'); }
  };
  const moveColumn = async (index: number, dir: number) => {
    const cols: FlowColumn[] = [...data.columns];
    const target = index + dir;
    if (target < 0 || target >= cols.length) return;
    [cols[index], cols[target]] = [cols[target], cols[index]];
    try { await flowFetch('/api/flow/columns/reorder', { method: 'PUT', body: JSON.stringify({ columnIds: cols.map(c => c.id) }) }); load(); }
    catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao reordenar'); }
  };
  const addColumn = async () => {
    if (!newColName.trim()) return;
    try {
      await flowFetch('/api/flow/columns', { method: 'POST', body: JSON.stringify({ boardId, name: newColName.trim(), kind: newColKind }) });
      setAddingColumn(false); setNewColName(''); setNewColKind('TODO');
      load();
    } catch (err) { toast.error(err instanceof Error ? err.message : 'Erro ao criar coluna'); }
  };

  if (loading || !data) {
    return <div className="flex justify-center py-12"><div className="h-8 w-8 animate-spin rounded-full border-b-2 border-cyan-600"></div></div>;
  }

  const { board, area, columns, tasks } = data as { board: any; area: any; columns: FlowColumn[]; tasks: FlowTask[] };
  const filtered = applyFilters(tasks);
  const tasksById = Object.fromEntries(tasks.map(task => [task.id, task])) as Record<string, FlowTask>;
  const filteredIds = new Set(filtered.map(task => task.id));
  const visibleTaskOrder: Record<string, string[]> = {};
  columns.forEach(column => {
    visibleTaskOrder[column.id] = (taskOrder[column.id] || []).filter(taskId => filteredIds.has(taskId));
  });
  const activeTask = activeTaskId ? tasksById[activeTaskId] : null;

  return (
    <div>
      {/* Cabeçalho do quadro */}
      <div className="mb-4 flex flex-wrap items-center gap-2">
        <Link to={`/flow/areas/${board.areaId}`} className="text-sm text-slate-400 hover:text-slate-600">← {area?.name || 'Área'}</Link>
        <span className="flex h-8 w-8 items-center justify-center rounded-lg text-sm font-black text-white" style={{ background: area?.color || '#0ea5e9' }}>
          {(area?.name || 'Q').charAt(0).toUpperCase()}
        </span>
        <h2 className="text-lg font-bold text-slate-900">{board.name}</h2>
      </div>

      {/* Filtros */}
      <div className="mb-4 flex flex-wrap gap-2">
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Buscar no quadro..."
          className="w-52 rounded-lg border border-slate-300 px-3 py-1.5 text-sm" />
        <select value={assigneeFilter} onChange={e => setAssigneeFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">Responsável</option>
          {users.map(u => <option key={u.id} value={u.id}>{u.nome}</option>)}
        </select>
        <select value={priorityFilter} onChange={e => setPriorityFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">Prioridade</option>
          {Object.entries(PRIORITY_LABELS).map(([k, v]) => <option key={k} value={k}>{v}</option>)}
        </select>
        <select value={dueFilter} onChange={e => setDueFilter(e.target.value)} className="rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
          <option value="">Prazo</option>
          <option value="today">Hoje</option>
          <option value="overdue">Atrasadas</option>
          <option value="none">Sem prazo</option>
        </select>
      </div>

      {/* Board */}
      <DndContext
        sensors={sensors}
        collisionDetection={closestCenter}
        onDragStart={handleDragStart}
        onDragEnd={handleDragEnd}
      >
        <div className="flex gap-3 overflow-x-auto pb-4">
          {columns.map((col, i) => {
          const taskIds = visibleTaskOrder[col.id] || [];
          return (
            <div key={col.id} className="flex w-72 shrink-0 flex-col">
              <ColumnHeader column={col} count={taskIds.length} isAdmin={isAdmin}
                onRename={(name) => renameColumn(col.id, name)}
                onChangeKind={(kind) => changeKind(col.id, kind)}
                onArchive={() => archiveColumn(col.id)}
                onMove={(dir) => moveColumn(i, dir)} />
              <TaskColumnBody
                columnId={col.id}
                taskIds={taskIds}
                tasksById={tasksById}
                onOpen={setDetailId}
                onAdd={() => setCreateCol(col.id)}
              />
            </div>
          );
          })}

        {/* Adicionar coluna (admin) */}
          {isAdmin && (
          <div className="w-72 shrink-0">
            {addingColumn ? (
              <div className="rounded-xl border border-slate-200 bg-white p-3 shadow-sm">
                <input autoFocus value={newColName} onChange={e => setNewColName(e.target.value)}
                  onKeyDown={e => e.key === 'Enter' && addColumn()}
                  placeholder="Nome da coluna" className="mb-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm" />
                <select value={newColKind} onChange={e => setNewColKind(e.target.value)} className="mb-2 w-full rounded-lg border border-slate-300 px-2 py-1.5 text-sm">
                  {KIND_OPTIONS.map(k => <option key={k.value} value={k.value}>Comporta como: {k.label}</option>)}
                </select>
                <div className="flex gap-2">
                  <button onClick={addColumn} className="flex-1 rounded-lg bg-cyan-600 py-1.5 text-sm font-semibold text-white hover:bg-cyan-700">Criar</button>
                  <button onClick={() => setAddingColumn(false)} className="rounded-lg border border-slate-300 px-3 py-1.5 text-sm text-slate-600">×</button>
                </div>
              </div>
            ) : (
              <button onClick={() => setAddingColumn(true)}
                className="w-full rounded-xl border border-dashed border-slate-300 py-3 text-sm font-semibold text-slate-400 hover:border-cyan-400 hover:text-cyan-600">
                + Adicionar coluna
              </button>
            )}
          </div>
          )}
        </div>
        <DragOverlay>
          {activeTask ? (
            <div className="w-72 rotate-1 rounded-xl border border-cyan-300 bg-white p-3 shadow-xl">
              <p className="text-sm font-semibold text-slate-800">{activeTask.title}</p>
            </div>
          ) : null}
        </DragOverlay>
      </DndContext>

      {createCol && (
        <TaskFormModal areas={areas} users={users} fixedBoardId={boardId} fixedColumnId={createCol}
          onClose={() => setCreateCol(null)} onSaved={load} />
      )}
      {detailId && (
        <TaskDetailModal taskId={detailId} areas={areas} users={users} onClose={() => setDetailId(null)} onChanged={load} />
      )}
    </div>
  );
}
